import type { DatabaseBlockModel } from '@blocksuite/affine-model';
import { getSelectedModelsCommand } from '@blocksuite/affine-shared/commands';
import { TelemetryProvider } from '@blocksuite/affine-shared/services';
import type { BlockStdScope } from '@blocksuite/std';
import type { BlockModel, Store } from '@blocksuite/store';
import { PublicationError } from '@streamlain/doc-publication-contract';

import {
  createDatabaseBlockDataSource,
  databaseViewInitTemplate,
  provisionShardedDatabaseBlockDataSource,
} from './data-source';

export interface InsertDatabaseBlockInput {
  readonly selectedModels?: BlockModel[];
  readonly viewType: string;
  readonly place?: 'after' | 'before';
  readonly removeEmptyLine?: boolean;
}
const pendingInsertions = new WeakMap<Store, Promise<string | null>>();

/** Page placement is captured before the storage ACK, never after it. */
export async function insertDatabaseBlock(
  std: BlockStdScope,
  input: InsertDatabaseBlockInput
): Promise<string | null> {
  const previous = pendingInsertions.get(std.store);
  if (previous) return previous;
  const { selectedModels, viewType, place, removeEmptyLine } = input;
  const target =
    place === 'before' ? selectedModels?.[0] : selectedModels?.at(-1);
  if (!target) return null;
  const parent = std.store.getParent(target);
  if (!parent) throw new PublicationError('FORMAT_MISMATCH');
  const parentId = parent.id;
  const index =
    parent.children.findIndex(child => child.id === target.id) +
    (place === 'before' ? 0 : 1);
  const pending = (async () => {
    const provisioned = await provisionShardedDatabaseBlockDataSource(
      std.store,
      {
        parentId,
        addBlock: (_flavour, props) => {
          const currentParent = std.store.getModelById(parentId);
          if (!currentParent) throw new PublicationError('FORMAT_MISMATCH');
          let id: string | undefined;
          std.store.captureSync();
          std.store.transact(() => {
            id = std.store.addBlock(
              'affine:database',
              props,
              currentParent,
              Math.min(index, currentParent.children.length)
            );
            const currentTarget = std.store.getModelById(target.id);
            if (removeEmptyLine && currentTarget?.text?.length === 0)
              std.store.deleteBlock(currentTarget);
          });
          std.store.captureSync();
          return id;
        },
        initialEmptyRow: true,
        initDataSource: source => databaseViewInitTemplate(source, viewType),
      }
    );
    if (provisioned.status === 'discarded') return null;
    provisioned.dataSource.dispose();
    return provisioned.databaseBlockId;
  })();
  pendingInsertions.set(std.store, pending);
  try {
    return await pending;
  } finally {
    if (pendingInsertions.get(std.store) === pending)
      pendingInsertions.delete(std.store);
  }
}

const pendingEntryActions = new WeakMap<Store, Promise<void>>();
/** Both UI entrypoints share selection, reporting and duplicate suppression. */
export function insertSelectedDatabaseBlock(
  std: BlockStdScope,
  viewType: string
): Promise<void> {
  const previous = pendingEntryActions.get(std.store);
  if (previous) return previous;
  const [, { selectedModels }] = std.command.exec(getSelectedModelsCommand);
  const pending = insertDatabaseBlock(std, {
    selectedModels,
    viewType,
    place: 'after',
    removeEmptyLine: true,
  })
    .then(id => {
      if (id)
        std
          .getOptional(TelemetryProvider)
          ?.track('BlockCreated', { blockType: 'affine:database' });
    })
    .catch(error => {
      console.error('Failed to create database:', error);
    })
    .finally(() => {
      pendingEntryActions.delete(std.store);
    });
  pendingEntryActions.set(std.store, pending);
  return pending;
}

export const initDatabaseBlock = (
  doc: Store,
  model: BlockModel,
  databaseId: string,
  viewType: string,
  isAppendNewRow = true
) => {
  const blockModel = doc.getBlock(databaseId)?.model as
    | DatabaseBlockModel
    | undefined;
  if (!blockModel) {
    return;
  }
  const datasource = createDatabaseBlockDataSource(blockModel);
  databaseViewInitTemplate(datasource, viewType);
  if (isAppendNewRow) {
    const parent = doc.getParent(model);
    if (!parent) return;
    doc.addBlock('affine:paragraph', {}, parent.id);
  }
};
